import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { KeyboardAvoidingView, Platform, View, Pressable, Image, RefreshControl, FlatList, useWindowDimensions, ActivityIndicator, Alert } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { Text, Title } from '@constants/Themed';
import styles, { getModernCardStyle } from '@stylesheet';
import { AntDesign, MaterialIcons, FontAwesome } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import toTitleCase from '@hooks/toTitleCase';
import moment from 'moment';
import SwitchWithIcons from 'react-native-switch-with-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apis, { acceptMessageRequest, rejectMessageRequest } from '@api/api';
import parseByDelimiter from '@utils/parseByDelimiter';
import { useSelector, useDispatch } from 'react-redux';
import AnimatedLottieView from '@utils/lottieAnimation';
import getDaysAgo from '@utils/getDaysAgo';
import LottieView from '@utils/lottieAnimation';
import { useDeviceType } from '@hooks/useDeviceType';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import { H5, Separator, XStack, YStack, SizableText, H4, Card, Popover } from 'tamagui';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';

const InvitationItem = ({ item, index, filter, filteredData, _refresh, refetch, queryClient, dispatch }) => {
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const { width } = useWindowDimensions();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const theme = themeSelector();

  // Responsive variables
  const isSmallScreen = width < 600;
  const showCompactMode = isSmallScreen;


  // Action handlers
  const handleAccept = useCallback(async () => {
    setIsAccepting(true);
    try {
      if (item.type === 'data') {
        await apis.acceptInvitation(item._id);
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: 'Data invitation accepted successfully!', type: 'success' }
        });
      } else if (item.type === 'company') {
        // Company invitation - user joins organization
        await apis.acceptCompanyInvitation({ id: item._id });
        // Invalidate company-related queries to refresh company list in CompanySelector
        await queryClient.invalidateQueries({ queryKey: ['companyList'] });
        await queryClient.invalidateQueries({ queryKey: ['companyMembers'] });
        // Refresh user data to get updated company list
        await queryClient.refetchQueries({ queryKey: ['user'] });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: 'You have successfully joined the organization! Switch to it from the company selector.', type: 'success' }
        });
      } else if (item.type === 'user') {
        // User invitation - adds to connectedUsers (acts as company context)
        // IMPORTANT: This does NOT change session context - user must manually switch via CompanySelector
        await apis.acceptCompanyInvitation({ id: item._id });

        // Only invalidate queries that need updating - do NOT touch session
        // Session remains unchanged - user stays in their current context
        await queryClient.invalidateQueries({ queryKey: ['companyList'] }); // Show new connected user in selector
        await queryClient.invalidateQueries({ queryKey: ['user'] }); // Refresh user data

        // Invalidate all-users to refresh messaging list with correct session (original user's contacts)
        // Use refetchType: 'active' to ensure Messages component immediately refetches
        await queryClient.invalidateQueries({
          queryKey: ['all-users'],
          refetchType: 'active' // Immediately refetch active queries (like Messages component)
        });

        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: 'User connection accepted! You can switch to their context from the company selector.', type: 'success' }
        });
      } else if (item.type === 'message') {
        await acceptMessageRequest(item._id);
        // Invalidate all-users to refresh messaging list - use refetchType: 'active' for immediate refetch
        await queryClient.invalidateQueries({
          queryKey: ['all-users'],
          refetchType: 'active' // Immediately refetch active queries (like Messages component)
        });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: 'Message request accepted!', type: 'success' }
        });
      }
      // Use the invalidate function to refresh data (but skip all-users for user type as we already handled it)
      if (item.type !== 'user' && item.type !== 'message') {
        await _refresh();
      } else {
        // For user and message types, only refresh invitations list
        await refetch();
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: 'Failed to accept invitation. Please try again.',
          type: 'error',
          details: error.message 
        } 
      });
    } finally {
      setIsAccepting(false);
    }
  }, [item._id, item.type, _refresh, queryClient, dispatch]);

  const handleReject = useCallback(async () => {
    setIsRejecting(true);
    try {
      if (!filter) {
        if (item.type === 'message') {
          await rejectMessageRequest(item._id);
        } else {
          await apis.rejectInvitation(item._id);
        }
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: 'Invitation declined', type: 'success' }
        });
      } else {
        await apis.revokeInvitation(item._id);
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: 'Invitation revoked', type: 'success' }
        });
      }
      // Use the invalidate function to refresh data
      await _refresh();
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Failed to ${filter ? 'revoke' : 'decline'} invitation. Please try again.`,
          type: 'error',
          details: error.message 
        } 
      });
    } finally {
      setIsRejecting(false);
    }
  }, [item._id, item.type, filter, _refresh, dispatch]);

  return useMemo(() => (
    <Animatable.View
      animation="fadeInUp"
      duration={600}
      delay={index * 100}
      style={[
        styles.modernCard,
        {
          backgroundColor: theme ? '#f1f5f9' : '#1e293b',
          marginHorizontal: 0,
          minHeight: showCompactMode ? 280 : 320,
          ...(Platform.OS === 'web' && { boxSizing: 'border-box' }),
        }
      ]}
    >
      <View style={{ minHeight: 100, overflow: "visible" }}>
        {/* Unread indicator */}
        {!item.viewed && (
          <View style={{
            position: 'absolute',
            zIndex: 1,
            top: 12,
            right: 12,
            backgroundColor: '#f59e0b',
            borderRadius: 6,
            minHeight: 12,
            minWidth: 12,
            elevation: 5,
            shadowColor: '#f59e0b',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.3,
            shadowRadius: 4,
          }} />
        )}

        {/* Header Section */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: showCompactMode ? 12 : 16,
          borderBottomWidth: 1,
          borderBottomColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        }}>
          {/* Avatar */}
          <AvatarHandler
            avatar={filter ? item.to.profile_information?.avatar?.img : item.from.profile_information?.avatar?.img}
            avatarImageStyle={{
              width: showCompactMode ? 36 : 44,
              height: showCompactMode ? 36 : 44,
              borderRadius: showCompactMode ? 18 : 22,
              borderWidth: 2,
              borderColor: theme ? '#e2e8f0' : '#334155',
            }}
            replacementImageStyle={{
              width: showCompactMode ? 36 : 44,
              height: showCompactMode ? 36 : 44,
              borderRadius: showCompactMode ? 18 : 22,
            }}
          />

          {/* Title and Metadata */}
          <View style={{ flex: 1, marginLeft: 12 }}>
            <XStack alignItems="center" gap={8}>
              <H4
                numberOfLines={1}
                color={theme ? "#1e293b" : "#f1f5f9"}
                fontSize={showCompactMode ? 16 : 18}
                fontWeight="600"
                flex={1}
              >
                {parseByDelimiter(item.title ||
                  item.type === 'message' ? 'Message Request' :
                  `Invited to join ${item.type}`
                )}
              </H4>
            </XStack>

            <XStack alignItems="center" gap={8} marginTop={4}>
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                Invite {filter ? 'sent' : 'received'}: {getDaysAgo(item.sent)}
              </SizableText>
              <View style={{
                width: 4,
                height: 4,
                borderRadius: 2,
                backgroundColor: theme ? "#64748b" : "#94a3b8",
              }} />
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
                textTransform="capitalize"
              >
                {item.type}
              </SizableText>
            </XStack>
          </View>

          {/* Actions Menu */}
          <XStack alignItems="center" gap={8}>
            {/* Accept Button (only for received invites) */}
            {!filter && (
              <Pressable
                onPress={handleAccept}
                disabled={isAccepting}
                style={{
                  padding: 8,
                  borderRadius: 20,
                  backgroundColor: theme ? '#f0fdf4' : '#064e3b',
                  opacity: isAccepting ? 0.5 : 1,
                }}
              >
                {isAccepting ? (
                  <ActivityIndicator size="small" color={theme ? "#059669" : "#10b981"} />
                ) : (
                    <AntDesign
                      name="check"
                      size={16}
                      color={theme ? "#059669" : "#10b981"}
                    />
                )}
              </Pressable>
            )}

            {/* Reject/Revoke Button */}
            <Pressable
              onPress={handleReject}
              disabled={isRejecting}
              style={{
                padding: 8,
                borderRadius: 20,
                backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
                opacity: isRejecting ? 0.5 : 1,
              }}
            >
              {isRejecting ? (
                <ActivityIndicator size="small" color="#ef4444" />
              ) : (
                  <AntDesign
                    name="close"
                    size={16}
                    color="#ef4444"
                  />
              )}
            </Pressable>
          </XStack>
        </View>

        {/* Content Section */}
        <View style={{ flex: 1, padding: showCompactMode ? 12 : 16 }}>
          {/* User Information */}
          <XStack gap={12} marginBottom={16}>
            <YStack flex={1}>
              <SizableText
                fontSize={showCompactMode ? 16 : 18}
                fontWeight="600"
                color={theme ? "#1e293b" : "#f1f5f9"}
                marginBottom={4}
              >
                {filter ? item.to?.profile_information ?
                  `${toTitleCase(item.to.profile_information.name)} ${toTitleCase(item.to.profile_information.surname)}` :
                  item.to :
                  item.from?.profile_information ?
                    `${toTitleCase(item.from.profile_information.name)} ${toTitleCase(item.from.profile_information.surname)}` :
                    item.from
                }
              </SizableText>
              <SizableText
                fontSize={12}
                color={theme ? "#64748b" : "#94a3b8"}
              >
                {filter ? 'Recipient' : 'From'}
              </SizableText>
            </YStack>
          </XStack>

          {/* Invitation Details */}
          <YStack gap={8}>
            {item.type === 'data' && (
              <XStack alignItems="center" gap={8}>
                <MaterialIcons
                  name="storage"
                  size={16}
                  color={theme ? "#3b82f6" : "#60a5fa"}
                />
                <SizableText
                  fontSize={14}
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  Entries: {item.entries || 'N/A'}
                </SizableText>
              </XStack>
            )}

            {item.type === 'project' && (
              <YStack gap={4}>
                <XStack alignItems="center" gap={8}>
                  <MaterialIcons
                    name="people"
                    size={16}
                    color={theme ? "#059669" : "#10b981"}
                  />
                  <SizableText
                    fontSize={14}
                    color={theme ? "#64748b" : "#94a3b8"}
                  >
                    Participants: {item.participants || 'N/A'}
                  </SizableText>
                </XStack>
                {item.startDate && (
                  <XStack alignItems="center" gap={8}>
                    <MaterialIcons
                      name="schedule"
                      size={16}
                      color={theme ? "#7c3aed" : "#a78bfa"}
                    />
                    <SizableText
                      fontSize={14}
                      color={theme ? "#64748b" : "#94a3b8"}
                    >
                      {moment(item.startDate).format(globalDate)} - {moment(item.endDate).format(globalDate)}
                    </SizableText>
                  </XStack>
                )}
              </YStack>
            )}

            {item.type === 'company' && item.users && (
              <XStack alignItems="center" gap={8}>
                <MaterialIcons
                  name="business"
                  size={16}
                  color={theme ? "#f59e0b" : "#fbbf24"}
                />
                <SizableText
                  fontSize={14}
                  color={theme ? "#64748b" : "#94a3b8"}
                >
                  {item.users}
                </SizableText>
              </XStack>
            )}

            {item.type === 'message' && (
              <YStack gap={4}>
                <XStack alignItems="center" gap={8}>
                  <MaterialIcons
                    name="message"
                    size={16}
                    color={theme ? "#3b82f6" : "#60a5fa"}
                  />
                  <SizableText
                    fontSize={14}
                    color={theme ? "#64748b" : "#94a3b8"}
                  >
                    Message Request
                  </SizableText>
                </XStack>
                {item.message && (
                  <View style={{
                    backgroundColor: theme ? '#f1f5f9' : '#1e293b',
                    padding: 8,
                    borderRadius: 6,
                    marginTop: 4,
                  }}>
                    <SizableText
                      fontSize={12}
                      color={theme ? "#64748b" : "#94a3b8"}
                      fontStyle="italic"
                    >
                      "{item.message}"
                    </SizableText>
                  </View>
                )}
              </YStack>
            )}
          </YStack>
        </View>

        {/* Action Buttons */}
        <View style={{
          flexDirection: showCompactMode ? 'column' : 'row',
          padding: showCompactMode ? 12 : 16,
          gap: showCompactMode ? 8 : 12,
          borderTopWidth: 1,
          borderTopColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        }}>
          {/* Status Badge */}
          <View style={{
            flex: showCompactMode ? undefined : 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 8,
            borderRadius: 8,
            backgroundColor: theme ? '#f1f5f9' : '#1e293b',
          }}>
            <MaterialIcons
              name={filter ? "send" : "inbox"}
              size={16}
              color={theme ? "#64748b" : "#94a3b8"}
            />
            <SizableText
              fontSize={12}
              color={theme ? "#64748b" : "#94a3b8"}
              marginLeft={4}
            >
              {filter ? 'Sent' : 'Received'}
            </SizableText>
          </View>

          {/* Primary Actions */}
          <XStack gap={8} flex={showCompactMode ? undefined : 2}>
            {!filter && (
              <Pressable
                onPress={handleAccept}
                disabled={isAccepting}
                style={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: theme ? '#f0fdf4' : '#064e3b',
                  opacity: isAccepting ? 0.5 : 1,
                }}
              >
                {isAccepting ? (
                  <ActivityIndicator size="small" color={theme ? '#059669' : '#10b981'} />
                ) : (
                  <>
                      <AntDesign
                        name="check"
                        size={16}
                        color={theme ? '#059669' : '#10b981'}
                      />
                      <SizableText
                        fontSize={12}
                        color={theme ? '#059669' : '#10b981'}
                        marginLeft={4}
                      >
                        Accept
                      </SizableText>
                  </>
                )}
              </Pressable>
            )}

            <Pressable
              onPress={handleReject}
              disabled={isRejecting}
              style={{
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 8,
                borderRadius: 8,
                backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
                opacity: isRejecting ? 0.5 : 1,
              }}
            >
              {isRejecting ? (
                <ActivityIndicator size="small" color="#ef4444" />
              ) : (
                <>
                    <AntDesign
                      name="close"
                      size={16}
                      color="#ef4444"
                    />
                    <SizableText
                      fontSize={12}
                      color="#ef4444"
                      marginLeft={4}
                    >
                      {filter ? 'Revoke' : 'Decline'}
                    </SizableText>
                </>
              )}
            </Pressable>
          </XStack>
        </View>
      </View>
    </Animatable.View>
  ), [item, filteredData, filter, theme, showCompactMode, handleAccept, handleReject, isAccepting, isRejecting])
}

const InvitationsScreen = () => {
  const [filter, setFilter] = useState(false);
  const userId = selectUser()?.id;
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['invitations'],
    queryFn: async () => {
      const res = await apis.getInvitations();
      return res;
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  // Memoize filtered data to prevent unnecessary re-renders
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    if (!filter) {
      // Show received invitations (where current user is the recipient)
      return data.filter(item => item.to?._id === userId);
    } else {
      // Show sent invitations (where current user is the sender)
      return data.filter(item => item.from?._id === userId);
    }
  }, [data, filter, userId]);

  const _refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Invalidate and refetch invitations after any mutation
  const invalidateInvitations = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['invitations'] });
    // Also invalidate all-users query to refresh messaging list (removes pending users)
    // Use refetchType: 'active' to ensure Messages component immediately refetches
    await queryClient.invalidateQueries({
      queryKey: ['all-users'],
      refetchType: 'active' // Immediately refetch active queries (like Messages component)
    });
    await refetch();
  }, [queryClient, refetch]);

  const searchBarConfig = {
    customStyle: { left: -10 },
    extraComponents: (
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingRight: 8,
        minHeight: 40,
      }}>
        {
          Platform.OS === 'web' &&
          <Text style={{
            minWidth: 80,
            textAlign: 'right',
            marginRight: 8,
          }}>
            {filter ? 'Sent' : 'Received'}
          </Text>
        }
        <View style={{
          minWidth: 60,
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <SwitchWithIcons
            key={`switch-${filter}`}
            value={filter}
            trackColor={{ false: '#cbd5e1', true: '#58BFEA' }}
            thumbColor={{ false: '#64748b', true: '#ffffff' }}
            animationDuration={150}
            icon={{ false: 'arrow-down', true: 'arrow-up' }}
            onValueChange={(value) => setFilter(value)}
          />
        </View>
      </View>
    )
  };
  const theme = themeSelector();
  return (
    <DefaultScreen backButton scrollDisabled noMargin style={{ flex: 1 }}
      searchBar={searchBarConfig}
    >
      <KeyboardAvoidingView style={{ flex: 1, paddingHorizontal: 20 }}>
        {isLoading ? (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <LottieView
              source={require("@assets/animations/40276-man-reviewing-business-analytics.json")}
              autoPlay
              loop
              style={{ height: 150, width: 150 }}
            />
            <H5 style={{ textAlign: "center", marginTop: 16 }}>Loading invitations...</H5>
          </View>
        ) : error ? (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <MaterialIcons name="error-outline" size={64} color="#ef4444" />
            <H5 style={{ textAlign: "center", marginTop: 16, color: "#ef4444" }}>Failed to load invitations</H5>
            <Text style={{ textAlign: "center", marginTop: 8, color: "#64748b" }}>Please try again</Text>
            <Pressable
              onPress={_refresh}
              style={{
                marginTop: 16,
                paddingHorizontal: 24,
                paddingVertical: 12,
                backgroundColor: '#3b82f6',
                borderRadius: 8,
              }}
            >
              <Text style={{ color: 'white', fontWeight: '600' }}>Retry</Text>
            </Pressable>
          </View>
        ) : (
              <FlatList
                contentContainerStyle={{ paddingHorizontal: 20, paddingTop: 10, paddingBottom: 150 }}
                showsVerticalScrollIndicator={false}
                style={{ marginTop: 10 }}
                data={filteredData}
                centerContent={filteredData?.length < 1}
                ItemSeparatorComponent={() => <View style={{ height: 12 }} />}
                refreshControl={
                  <RefreshControl
                    refreshing={isFetching}
                    onRefresh={_refresh}
                    colors={['#3b82f6']}
                    tintColor="#3b82f6"
                  />
                }
            ListEmptyComponent={() => (
              <View style={[getModernCardStyle(theme, 'medium'), { maxHeight: 400, paddingVertical: "5%", justifyContent: 'center', alignItems: 'center', gap: 10, flex: 1 }]}>
                <LottieView
                  source={require("@assets/animations/40276-man-reviewing-business-analytics.json")}
                  autoPlay
                  loop
                  style={[styles.centerEverything, { flex: 1, alignSelf: 'center', justifyContent: 'center', height: 150, width: 150, left: 10, alignContent: 'center', alignItems: 'center' }]}
                />
                <H5 style={[styles.centerEverything, { textAlign: "center" }]}>
                  {filter ? 'No invites sent' : 'No invites pending'}
                </H5>
                <Text style={{ textAlign: "center", flexWrap: "wrap" }}>
                  Try inviting some users to share with
                </Text>
              </View>
            )}
            renderItem={({ item, index }) => (
              <InvitationItem
                item={item}
                index={index}
                filter={filter}
                filteredData={filteredData}
                _refresh={invalidateInvitations}
                refetch={invalidateInvitations}
                queryClient={queryClient}
                dispatch={dispatch}
              />
            )}
          />
        )}
      </KeyboardAvoidingView>
    </DefaultScreen>
  )
}
export default InvitationsScreen;